import React from 'react';
import { Box, Typography, Autocomplete, TextField } from '@mui/material';
import { accountNameFromType, type Account } from '../../api/models/account.model';
import { useDispatch, useSelector } from 'react-redux';
import { useGetAccountsQuery } from '../../api/account.api';
import { selectAccount, setSelectedAccount } from '../../redux/slices/accountSlice';
import { ACCOUNT_LABEL, LOADING_WITH_ELLIPSIS, SELECT_AN_ACCOUNT } from '../../constants/messages';

const LesseeDropdown: React.FC = () => {
	const dispatch = useDispatch();

	const activeAccount = useSelector(selectAccount);

	// Fetch accounts using the RTK Query hook
	const { data: accounts, isLoading } = useGetAccountsQuery(undefined);

	const handleLesseeSelect = (option: Account) => {
		// Dispatch action to store the selected account in Redux
		dispatch(setSelectedAccount(option));
	};

	function isSingleItem(accounts: Account[] | undefined): boolean {
		return accounts ? accounts.length === 1 : false;
	}

	function label(accounts: Account[] | undefined): string {
		if (isLoading) {
			return LOADING_WITH_ELLIPSIS;
		}
		if (accounts && accounts.length === 1) {
			return ACCOUNT_LABEL;
		}
		return '';
	}

	React.useEffect(() => {
		if (accounts && accounts.length > 0) {
			handleLesseeSelect(accounts[0]);
		}
	}, [accounts, dispatch]);

	return (
		<Box
			sx={{
				display: 'flex',
				alignItems: 'center',
				width: '100%',
				justifyContent: 'end',
				paddingRight: 0,
			}}
		>
			<Typography
				variant="body1"
				sx={{
					mr: 1,
					fontWeight: 'bold',
					color: 'common.white',
					width: '6rem',
					margin: 0,
				}}
			>
				{label(accounts)}
			</Typography>
			{isSingleItem(accounts) || isLoading || !accounts ? (
				<Typography
					variant="body1"
					sx={{
						fontWeight: 500,
						color: 'common.white',
						mr: { xs: 4, sm: 0 },
					}}
				>
					{activeAccount?.accountName}
				</Typography>
			) : (
				<Autocomplete
					data-testid="main-nav-account-dropdown-input"
					disablePortal
					size="small"
					sx={{ width: '65%' }}
					options={accounts || []}
					getOptionLabel={(option) => accountNameFromType(option)}
					value={activeAccount}
					onChange={(_, newValue) => {
						if (newValue) {
							handleLesseeSelect(newValue);
						}
					}}
					isOptionEqualToValue={(option, value) => option.accountId === value.accountId}
					renderInput={(params) => (
						<TextField
							{...params}
							label={SELECT_AN_ACCOUNT}
							slotProps={{
								inputLabel: {
									sx: {
										color: 'common.white',
										top: -10,
										left: -10,
									},
								},
							}}
							sx={{ bgcolor: 'white', borderRadius: 1 }}
						/>
					)}
				/>
			)}
		</Box>
	);
};

export default LesseeDropdown;
